import React from "react";
import { Car, Bike, Truck, CheckCircle2 } from "lucide-react";

/**
 * Sélecteur du type de véhicule : 3 vignettes photo réalistes
 * (Voiture Peugeot 208 bleue / Moto roadster noir / Poids lourd bleu)
 */
export const VEHICLE_TYPES = [
  {
    id: "auto",
    label: "Voiture",
    description: "Auto, berline, SUV",
    icon: Car,
    image: "/images/vehicle-types/auto.jpg",
    imageAlt: "Voiture citadine moderne — représentation du type Auto",
    objectPosition: "center 50%",
    accent: {
      ring: "ring-cyan-500/50",
      bg: "bg-cyan-500/15",
      border: "border-cyan-500",
      iconColor: "text-cyan-300",
      glow: "shadow-[0_0_30px_rgba(6,182,212,0.35)]",
      badge: "text-cyan-200 ring-cyan-400/40",
    },
  },
  {
    id: "moto",
    label: "Moto",
    description: "Scooter & roadster",
    icon: Bike,
    image: "/images/vehicle-types/moto.jpg",
    imageAlt: "Moto roadster moderne — représentation du type Moto",
    objectPosition: "center 50%",
    accent: {
      ring: "ring-amber-400/50",
      bg: "bg-amber-500/15",
      border: "border-amber-400",
      iconColor: "text-amber-300",
      glow: "shadow-[0_0_30px_rgba(251,191,36,0.35)]",
      badge: "text-amber-200 ring-amber-400/40",
    },
  },
  {
    id: "poids_lourd",
    label: "Poids lourd",
    description: "Camion & utilitaire",
    icon: Truck,
    image: "/images/vehicle-types/poids-lourd.jpg",
    imageAlt:
      "Tracteur routier à cabine plate européenne — représentation du type Poids lourd",
    objectPosition: "center 50%",
    accent: {
      ring: "ring-indigo-400/50",
      bg: "bg-indigo-500/15",
      border: "border-indigo-400",
      iconColor: "text-indigo-300",
      glow: "shadow-[0_0_30px_rgba(129,140,248,0.35)]",
      badge: "text-indigo-200 ring-indigo-400/40",
    },
  },
];

export const VehicleTypeSelector = ({ value, onChange }) => {
  return (
    <div
      data-testid="vehicle-type-selector"
      className="w-full"
      role="radiogroup"
      aria-label="Type de véhicule"
    >
      <div className="mb-4 text-center">
        <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
          Étape 1 · Choisis ton type de véhicule
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        {VEHICLE_TYPES.map((t) => {
          const Icon = t.icon;
          const active = value === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`vehicle-type-card-${t.id}`}
              onClick={() => onChange(t.id)}
              className={`group relative flex aspect-[4/3] flex-col justify-end overflow-hidden rounded-2xl border-2 bg-slate-950 text-left transition-all duration-300 hover:-translate-y-0.5 ${
                active
                  ? `${t.accent.border} ring-2 ${t.accent.ring} ${t.accent.glow}`
                  : "border-slate-800 hover:border-slate-700"
              }`}
            >
              {/* Photo réelle du véhicule */}
              <img
                src={t.image}
                alt={t.imageAlt}
                loading="lazy"
                className={`absolute inset-0 h-full w-full object-cover transition-all duration-500 ${
                  active ? "scale-105 brightness-110" : "brightness-75 group-hover:brightness-95 group-hover:scale-105"
                }`}
                style={{ objectPosition: t.objectPosition }}
              />
              {/* Overlay dégradé pour lisibilité du texte */}
              <div
                className={`absolute inset-0 bg-gradient-to-t transition-opacity duration-300 ${
                  active
                    ? "from-slate-950/90 via-slate-950/40 to-slate-950/10"
                    : "from-slate-950 via-slate-950/60 to-slate-950/20"
                }`}
              />
              {/* Badge "Sélectionné" */}
              {active && (
                <span
                  className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-slate-950/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ring-1 backdrop-blur-sm ${t.accent.badge}`}
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Sélectionné
                </span>
              )}
              {/* Icône véhicule discret */}
              <span
                className={`absolute left-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-xl backdrop-blur-sm transition ${
                  active ? t.accent.bg : "bg-slate-950/50"
                }`}
              >
                <Icon
                  className={`h-5 w-5 ${
                    active ? t.accent.iconColor : "text-slate-200"
                  }`}
                  strokeWidth={1.8}
                />
              </span>
              {/* Labels en bas */}
              <div className="relative z-10 flex flex-col gap-0.5 p-4">
                <span
                  className={`font-heading text-lg font-extrabold uppercase tracking-wide text-slate-50 drop-shadow-lg sm:text-xl`}
                >
                  {t.label}
                </span>
                <span className="text-xs font-medium text-slate-300 drop-shadow">
                  {t.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
